import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PredefinedDateRanges from "../../../core/common/datePicker";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import Table from "../../../core/common/dataTable/index";
import { all_routes } from "../../router/all_routes";
import { projectDetails } from "../../../core/data/json/projectDetails";
import { taskDetails } from "../../../core/data/json/taskDetails";
import ReactApexChart from "react-apexcharts";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import { fetchAllTaskList, fetchAllTaskcount } from "../../../core/data/redux/reports/task_report/task.Action";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import { exportToCSV, exportToExcel } from "../../../core/common/GlobalFunctions/exportTableData";
import { cl } from "@fullcalendar/core/internal-common";
import { filterData } from "../../../core/common/GlobalFunctions/tableFilters";
import { formatDate } from "../../../core/utils/configFormatters";
import { useAppConfig } from "../../../customhooks/useAppConfig";

interface TaskReportProps {
  showOnlyTable?: boolean;
}
const TaskReport: React.FC<TaskReportProps> = ({ showOnlyTable}) => {
  const [count, setcount] = useState<any>(null);
  const [tableKey, setTableKey] = useState(0);
  const [tasklist, settasklist] = useState<any>(null);
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedSort, setSelectedSort] = useState<any>("");

  const dispatch = useDispatch<AppDispatch>();

  const data = tasklist ? tasklist : [];

  const filtereddata = useMemo(() => {
    const filters =
      selectedStatus && selectedStatus !== "All"
        ? { Priority: selectedStatus }
        : {};
    return filterData(data, filters, "CreatedDate", "CreatedDate", selectedSort);
  }, [dispatch, tasklist, selectedSort, selectedStatus]);



  useEffect(() => {
    setTableKey((prevKey) => prevKey + 1);
  }, [dispatch, tasklist, selectedSort, selectedStatus]);

  useEffect(() => {
    dispatch(fetchAllTaskcount()).then((res) => {
      setcount(res.payload.data);
    });
    dispatch(fetchAllTaskList()).then((res) => {
      settasklist(res.payload.data);
    });
  }, []);



  useEffect(() => {
    if (count && count.length > 0) {
      const {
        highPriorityCount,
        mediumPriorityCount,
        lowPriorityCount,
      } = count[0];

      const updatedSeries = [
        parseInt(highPriorityCount || 0),
        parseInt(mediumPriorityCount || 0),
        parseInt(lowPriorityCount || 0),
      ];

      settask((prev: any) => ({
        ...prev,
        series: updatedSeries,
        labels: ["High Priority", "Medium Priority", "Low Priority"],
      }));
    } else {
      settask((prev: any) => ({
        ...prev,
        series: [0, 0, 0],
        labels: ["High Priority", "Medium Priority", "Low Priority"],
      }));
    }
  }, [count]);

  const getPercent = (value: number | string, total: number | string) =>
    total ? Math.round((+value / +total) * 100) : 0;

  const columns = [
    {
      title: "Task Name",
      dataIndex: "TaskName",
      render: (text: String) => (
        <div className="d-flex align-items-center file-name-icon">
          <h6 className="fw-medium">
            <Link to="#">{text}</Link>
          </h6>
        </div>
      ),
      sorter: (a: any, b: any) => a.TaskName.length - b.TaskName.length,
    },
    {
      title: "Project Name",
      dataIndex: "ProjectName",
      sorter: (a: any, b: any) => a.ProjectName.length - b.ProjectName.length,
    },
    {
      title: "Created Date",
      dataIndex: "CreatedDate",
      sorter: (a: any, b: any) => new Date(a.CreatedDate).getTime() - new Date(b.CreatedDate).getTime(),
      render: (text: string) => formatDate(text, config),
    },
    {
      title: "Due Date",
      dataIndex: "DueDate",
      render: (text: string) => formatDate(text, config),
      sorter: (a: any, b: any) => a.DueDate.length - b.DueDate.length,
    },
    {
      title: "Priority",
      dataIndex: "Priority",
      render: (text: String, record: any) => (
        <span
          className={`badge  ${text === "Low"
            ? "badge-success-transparent"
            : text === "Medium"
              ? "badge-warning-transparent"
              : "badge-danger-transparent"
            }`}
        >
          <i className="ti ti-point-filled me-1"></i>
          {text}
        </span>
      ),
      sorter: (a: any, b: any) => a.Priority.length - b.Priority.length,
    },
    {
      title: "Status",
      dataIndex: "Status",
      render: (text: String, record: any) => (
        <span
          className={`badge  d-inline-flex align-items-center badge-xs ${text === "Completed"
            ? "badge-success"
            : text === "Inprogress"
              ? "badge-purple"
              : text === "On Hold"
                ? "badge-warning"
                : "badge-skyblue"
            }`}
        >
          <i className="ti ti-point-filled me-1" />
          {text}
        </span>
      ),
      sorter: (a: any, b: any) => a.Status.length - b.Status.length,
    },
  ];

  const [taskchart, settask] = useState<any>({
    series: [0, 0, 0],
    chart: {
      type: "donut",
      width: 220,
    },
    colors: ["#FF4C51", "#FFC107", "#03C95A"],
    labels: ["High Priority", "Medium Priority", "Low Priority"],
    plotOptions: {
      pie: {
        startAngle: -90,
        endAngle: 270,
        stroke: {
          show: true,
          width: 10,
          colors: ["#FFFFFF"],
        },
        donut: {
          size: "80%",
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    legend: {
      show: false,
    },
    annotations: {
      position: "front",
      style: {
        fontSize: "24px",
        fontWeight: "bold",
        color: "#000000",
      },
      text: {
        text: "+14%",
        background: {
          enabled: true,
          foreColor: "#FFFFFF",
          border: "#000000",
          borderWidth: 1,
          borderRadius: 2,
          opacity: 0.7,
        },
      },
      x: "50%",
      y: "50%",
    },
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 200,
          },
          legend: {
            show: false,
          },
        },
      },
    ],
  });

  const config = useAppConfig();
  if (!config) return null;


  if (showOnlyTable) {
  return (
   <Table
    key={tableKey}
    dataSource={filtereddata}
    columns={columns}
    Selection={true}
  />
  );
}
  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Task Report</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">HR</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Task Report
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              <div className="mb-2">
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    <i className="ti ti-file-export me-1" />
                    Export
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <button
                        onClick={() => exportToCSV(data, "Task_report")}
                        className="dropdown-item rounded-1"
                      >
                        <i className="ti ti-file-type-pdf me-1" />
                        Export as CSV
                      </button>
                    </li>
                    <li>
                      <button
                        onClick={() => exportToExcel(data, "Task_report")}
                        className="dropdown-item rounded-1"
                      >
                        <i className="ti ti-file-type-xls me-1" />
                        Export as Excel{" "}
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}
          <div className="row">
            {/* Total Exponses */}
            <div className="col-lg-6 col-md-6 d-flex">
              <div className="row flex-fill g-2">
                <div className="col-lg-6 col-md-6 d-flex">
                  <div className="card flex-fill">
                    <div className="card-body ">
                      <div className="row align-items-center">
                        <div className="col-8">
                          <div>
                            <span className="fs-14 fw-normal text-truncate mb-1">
                              Total Tasks
                            </span>
                            <h5>{count?.[0].totalCount}</h5>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-6 col-md-6 d-flex">
                  <div className="card flex-fill">
                    <div className="card-body ">
                      <div className="row align-items-center">
                        <div className="col-8">
                          <div>
                            <span className="fs-14 fw-normal text-truncate mb-1">
                              Total Project
                            </span>
                            <h5>{count?.[0].totalProjects}</h5>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-6 col-md-6 d-flex">
                  <div className="card flex-fill">
                    <div className="card-body ">
                      <div className="row align-items-center">
                        <div className="col-8">
                          <div>
                            <span className="fs-14 fw-normal text-truncate mb-1">
                              Task Board
                            </span>
                            <h5>{count?.[0].totalTaskBoards}</h5>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-6 col-md-6 d-flex"></div>
              </div>
            </div>
            {/* /Total Exponses */}
            {/* Total Exponses */}
            <div className="col-lg-6 col-md-6 d-flex">
              <div className="card flex-fill">
                <div className="card-header border-0">
                  <div className="d-flex flex-wrap justify-content-between align-items-center">
                    <div className="d-flex align-items-center ">
                      <span className="me-2">
                        <i className="ti ti-chart-pie text-danger" />
                      </span>
                      <h5>Tasks</h5>
                    </div>
                  </div>
                </div>
                <div className="card-body pt-0">
                  <div className="row align-items-center">
                    <div className="col-md-6 d-flex align-items-center justify-content-center">
                      <div className="position-relative payment-total">
                        <ReactApexChart
                          options={taskchart}
                          series={taskchart.series}
                          type="donut"
                          height={210}
                          width={220}
                        />
                        {/* <div className="task-total-content ">
                          <p className="fs-16 fw-normal mb-0">Pending</p>
                          <span className="display-3 fs-24 fw-bold text-skyblue">
                            {getPercent(
                              count?.[0].pendingCount,
                              count?.[0].totalCount
                            )}
                            %
                          </span>
                        </div> */}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="row gy-4">
                        <div className="col-md-6">
                          <div className="d-flex task-report-icons">
                            <span className="me-2">
                              <i
                                className="ti ti-arrow-badge-right-filled"
                                style={{ color: "#FF4C51" }}
                              />
                            </span>
                            <h6 className="fs-16">
                              High Priority{" "}
                              <span className="fs-14 fw-normal">
                                {count?.[0].highPriorityCount}
                              </span>
                            </h6>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="d-flex task-report-icons">
                            <span className="me-2">
                              <i className="ti ti-arrow-badge-right-filled"  style={{ color: "#FFC107" }} />
                            </span>
                            <h6 className="fs-16">
                              Medium Priority{" "}
                              <span className="fs-14 fw-normal">
                                {count?.[0].mediumPriorityCount}
                              </span>
                            </h6>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="d-flex task-report-icons">
                            <span className="me-2">
                              <i className="ti ti-arrow-badge-right-filled "  style={{ color:  "#03C95A" }}/>
                            </span>
                            <h6 className="fs-16">
                              Low Priority{" "}
                              <span className="fs-14 fw-normal">
                                {count?.[0].lowPriorityCount}
                              </span>
                            </h6>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* /Total Exponses */}
          </div>
          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Tasks List</h5>
              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="dropdown me-3">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    {selectedStatus
                      ? selectedStatus.charAt(0).toUpperCase() +
                      selectedStatus.slice(1)
                      : "Select Type"}
                  </Link>
                  <ul className="dropdown-menu dropdown-menu-end p-3">
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedStatus("All")}
                        className="dropdown-item rounded-1"
                      >
                        All
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedStatus("Medium")}
                        className="dropdown-item rounded-1"
                      >
                        Medium
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedStatus("High")}
                        className="dropdown-item rounded-1"
                      >
                        High
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedStatus("Low")}
                        className="dropdown-item rounded-1"
                      >
                        Low
                      </Link>
                    </li>
                  </ul>
                </div>
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    {selectedSort
                      ? `Sort By : ${selectedSort}`
                      : "Sort By : Last 7 Days"}
                  </Link>
                  <ul className="dropdown-menu dropdown-menu-end p-3">
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Recently Added")}
                        className="dropdown-item rounded-1"
                      >
                        Recently Added
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Ascending")}
                        className="dropdown-item rounded-1"
                      >
                        Ascending
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Descending")}
                        className="dropdown-item rounded-1"
                      >
                        Descending
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Last Month")}
                        className="dropdown-item rounded-1"
                      >
                        Last Month
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Last 7 Days")}
                        className="dropdown-item rounded-1"
                      >
                        Last 7 Days
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              <Table
                key={tableKey}
                dataSource={filtereddata}
                columns={columns}
                Selection={true}
              />
            </div>
          </div>
        </div>
        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025© hrmtree.</p>
          <p>
            Developed By{" "}
            <Link to="#" className="text-primary">
              Leadchain Systems
            </Link>
          </p>
        </div>
      </div>
      {/* /Page Wrapper */}
    </>
  );
};

export default TaskReport;
